import * as THREE from 'three'
import Experience from "../Experience";
import CtaBuilder from './components/CtaBuilder';
import TitleBuilder from './components/TitleBuilder';
import TextBuilder from './components/TextBuilder';
import ToolsSectionBuilder from './components/ToolsSectionBuilder';
import utils from '../Utils/utils';
import gsap from "gsap";

export default class About {

    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.ressources = this.experience.ressources
        this.time = this.experience.time
        this.camera = this.experience.camera.instance
        this.isPhone = this.experience.isPhone

        this.ctaBuilder = new CtaBuilder();
        this.titleBuilder = new TitleBuilder()
        this.textBuilder = new TextBuilder()

        this.contentTools = {
            text: this.experience.router.main_data.data.content_tools_text[0].text,
            images: this.experience.router.main_data.data.content_tools_images
        }
        this.liveTools = {
            text: this.experience.router.main_data.data.live_tools_text[0].text,
            images: this.experience.router.main_data.data.live_tools
        }
        this.realTimeTools = {
            text: this.experience.router.main_data.data.real_time_tools_text[0].text,
            images: this.experience.router.main_data.data.real_time_tools_images
        }
        this.stageTools = {
            text: this.experience.router.main_data.data.stage_tools_text[0].text,
            images: this.experience.router.main_data.data.stage_tools_images
        }
        this.aiTools = {
            text: this.experience.router.main_data.data.ai_tools_text[0].text,
            images: this.experience.router.main_data.data.ai_tools_images
        }

        this.tools = [
            this.liveTools,
            this.realTimeTools,
            this.stageTools,
            this.contentTools,
            this.aiTools
        ]

        this.setBalls()
        this.toolsSectionBuilder = new ToolsSectionBuilder(this)
        this.firstSectionPourcentage = 1
        this.secondSectionPourcentage = 0
        this.logoVisible = true
        this.partnerContainer = document.querySelector('.about-partnersContainer')
        document.querySelector('.about-text p').innerHTML = this.experience.router.main_data.data.about_text[0].text

        this.isDestroyed = false

        this.setPartnerCards()
        this.setLogo()
    }

    // ─── Main about-page logo ────────────────────────────────────────────────────
    setLogo() {
        this.logo = this.experience.ressources.items.brit_logo.scene.clone()
        this.logoHtmlTarget = document.querySelector('.about-top')
        this.logoItems = []
        this.logo.traverse((child) => {
            if (child.isMesh) {
                child.material = this.experience.redMaterial.clone()
                child.userData.random = Math.random()
                this.logoItems.push(child)
            }
        })
        this.logoScale = 0.18
        this.logo.scale.set(this.logoScale, this.logoScale, this.logoScale)
        this.logo.rotation.y = -0.2 + Math.PI
        this.scene.add(this.logo)
        this.positionLogo()
    }

    logoAppear() {
        if (!this.logoVisible) {
            this.logoVisible = true
            this.logo.visible = true
            this.logoItems.forEach((item) => {
                gsap.to(item.material.uniforms.u_alpha, { value: 1, duration: 1, ease: "power3.inOut" })
            })
        }
    }

    logoDisappear() {
        if (this.logoVisible) {
            this.logoVisible = false
            gsap.to(this.logo.scale, {
                x: 0.5, y: 0.5, z: 0.5, duration: 1, ease: "power3.inOut",
                onComplete: () => { this.logo.visible = false }
            })
            this.logoItems.forEach((item) => {
                gsap.to(item.material.uniforms.u_alpha, { value: 0, duration: 1, ease: "power3.inOut" })
            })
        }
    }

    positionLogo(e = null) {
        const wPos = utils.getWorldPositionFromElement(this.logoHtmlTarget, this.camera, 3)
        let offsetedPos = !this.isPhone
            ? utils.remToWorldOffset(-600, -100, this.camera, 3)
            : utils.remToWorldOffset(0, -400, this.camera, 3)
        const secondSectionOffset = utils.remToWorldOffset(-150, -600, this.camera, 3)
        const scrollOffset = utils.pixelsToWorldOffset(window.scrollY, 0, this.camera, 3)
        this.logo.position.set(
            wPos.x - (offsetedPos.x * this.firstSectionPourcentage) + (secondSectionOffset.x * this.secondSectionPourcentage),
            wPos.y - (offsetedPos.y * this.firstSectionPourcentage) - scrollOffset.x - (secondSectionOffset.y * this.secondSectionPourcentage),
            0
        )
        if (e) {
            this.firstSectionPourcentage = Math.pow(Math.max(1 - (e.animatedScroll / window.innerHeight), 0), 3)
            this.secondSectionPourcentage = Math.max(Math.min((e.animatedScroll - window.innerHeight + 50) / window.innerHeight, 1), 0)
            const sc = utils.pixelsToWorldOffset(e.animatedScroll, 0, this.camera, 3)
            this.logo.position.set(
                wPos.x - (offsetedPos.x * this.firstSectionPourcentage) + (secondSectionOffset.x * this.secondSectionPourcentage),
                wPos.y - (offsetedPos.y * this.firstSectionPourcentage) - sc.x - (secondSectionOffset.y * this.secondSectionPourcentage),
                0
            )
        }
    }

    // ─── Team section debris ─────────────────────────────────────────────────────
    setBalls() {
        this.ballsGroup = new THREE.Group()
        this.ballsLittleGroups = []

        // Fresh hidden material
        const mat = () => {
            const m = this.experience.redMaterial.clone()
            m.uniforms.u_alpha.value = 0
            return m
        }

        // Pull models
        const britLogo = () => this.experience.ressources.items.brit_logo.scene.clone()
        const bars     = () => this.experience.ressources.items.bars.scene.clone()

        // ── Helper: explode a model into individually animated mesh pieces ────────
        //   Returns an array of THREE.Mesh nodes each parented to `parentGroup`,
        //   each with userData containing spin rates and a starting offset position.
        const explodeModel = (modelScene, parentGroup, {
            basePosSpread = 1.0,   // how far pieces scatter
            scaleRange    = [0.04, 0.09],
            spinRange     = 0.018,
            count         = null,  // optional limit on how many meshes to use
        } = {}) => {
            const meshes = []
            modelScene.traverse(c => { if (c.isMesh) meshes.push(c) })
            const limited = count ? meshes.slice(0, count) : meshes
            limited.forEach((src, i) => {
                const m = new THREE.Mesh(src.geometry, mat())
                const scale = scaleRange[0] + Math.random() * (scaleRange[1] - scaleRange[0])
                m.scale.setScalar(scale)
                // Scatter randomly in a circle
                const angle  = (i / limited.length) * Math.PI * 2 + Math.random() * 0.8
                const radius = 0.35 + Math.random() * basePosSpread
                m.position.set(
                    Math.cos(angle) * radius,
                    Math.sin(angle) * radius,
                    (Math.random() - 0.5) * 0.15
                )
                m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2)
                m.userData = {
                    spinX:        (Math.random() - 0.5) * spinRange,
                    spinY:        (Math.random() - 0.5) * spinRange,
                    spinZ:        (Math.random() - 0.5) * spinRange * 0.5,
                    bobAmp:       0.003 + Math.random() * 0.004,
                    bobPhase:     Math.random() * Math.PI * 2,
                    orbitAngle:   angle,
                    orbitRadius:  radius,
                    orbitSpeed:   (0.003 + Math.random() * 0.004) * (Math.random() > 0.5 ? 1 : -1),
                    random:       Math.random(),
                }
                parentGroup.add(m)
            })
            return limited
        }

        // ── Helper: add sharp primitive shards ───────────────────────────────────
        const addShards = (parentGroup, positions, geoFn, spinRange = 0.02) => {
            positions.forEach(([x, y, z], i) => {
                const mesh = new THREE.Mesh(geoFn(i), mat())
                mesh.position.set(x, y, z)
                mesh.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, 0)
                mesh.userData = {
                    spinX:      (Math.random() - 0.5) * spinRange,
                    spinY:      (Math.random() - 0.5) * spinRange * 1.5,
                    spinZ:      (Math.random() - 0.5) * spinRange * 0.4,
                    bobAmp:     0.003,
                    bobPhase:   Math.random() * Math.PI * 2,
                    orbitAngle: Math.atan2(y, x),
                    orbitRadius: Math.sqrt(x * x + y * y),
                    orbitSpeed: (0.002 + Math.random() * 0.003) * (i % 2 ? 1 : -1),
                    random:     Math.random(),
                }
                parentGroup.add(mesh)
            })
        }

        // ── Group 0 – Tony Liang: brit_logo exploded, sharp tetrahedra shards ────
        const g0 = new THREE.Group()
        explodeModel(britLogo(), g0, { basePosSpread: 0.7, scaleRange: [0.05, 0.11], spinRange: 0.022 })
        addShards(g0, [
            [-0.90,  0.55, 0.0], [ 0.85,  0.60, 0.0],
            [ 0.70, -0.65, 0.1], [-0.75, -0.55, 0.0],
            [ 0.10,  0.95, 0.0],
        ], (i) => new THREE.TetrahedronGeometry(0.06 + (i % 3) * 0.02, 0))
        this.ballsLittleGroups.push(g0); this.ballsGroup.add(g0)

        // ── Group 1 – Edwin Zhou: bars exploded wide, diamond shards (octa) ──────
        const g1 = new THREE.Group()
        explodeModel(bars(), g1, { basePosSpread: 0.9, scaleRange: [0.03, 0.07], spinRange: 0.016, count: 12 })
        addShards(g1, [
            [-0.85,  0.45, 0.05], [ 0.80,  0.50, 0.00],
            [ 0.90, -0.40, 0.00], [-0.80, -0.50, 0.05],
            [ 0.00, -0.90, 0.00], [-0.40,  0.80, 0.10],
        ], (i) => new THREE.OctahedronGeometry(0.055 + (i % 3) * 0.02, 0))
        this.ballsLittleGroups.push(g1); this.ballsGroup.add(g1)

        // ── Group 2 – Cary Cao: two brit_logos exploded at different radii ───────
        const g2 = new THREE.Group()
        explodeModel(britLogo(), g2, { basePosSpread: 0.5, scaleRange: [0.06, 0.10], spinRange: 0.020 })
        explodeModel(britLogo(), g2, { basePosSpread: 0.85, scaleRange: [0.03, 0.06], spinRange: 0.015 })
        addShards(g2, [
            [-0.95,  0.20, 0.0], [ 0.95,  0.20, 0.0],
            [ 0.50,  0.85, 0.0], [-0.50, -0.80, 0.0],
        ], (i) => new THREE.IcosahedronGeometry(0.05 + (i % 2) * 0.025, 0))
        this.ballsLittleGroups.push(g2); this.ballsGroup.add(g2)

        // ── Group 3 – Desmond Donnelly: bars + brit_logo mixed, box shards ───────
        const g3 = new THREE.Group()
        explodeModel(bars(), g3, { basePosSpread: 0.8, scaleRange: [0.03, 0.08], spinRange: 0.018, count: 10 })
        explodeModel(britLogo(), g3, { basePosSpread: 0.55, scaleRange: [0.04, 0.08], spinRange: 0.022, count: 6 })
        addShards(g3, [
            [-0.88,  0.55, 0.0], [ 0.88,  0.55, 0.0],
            [ 0.88, -0.55, 0.0], [-0.88, -0.55, 0.0],
        ], (i) => { const s = 0.06 + (i % 2) * 0.03; return new THREE.BoxGeometry(s, s, s) })
        this.ballsLittleGroups.push(g3); this.ballsGroup.add(g3)

        // ── Group 4 – Alan Pham: brit_logo fully exploded + icosa star ring ──────
        const g4 = new THREE.Group()
        explodeModel(britLogo(), g4, { basePosSpread: 0.75, scaleRange: [0.05, 0.12], spinRange: 0.025 })
        // Precise ring of 8 shards
        for (let i = 0; i < 8; i++) {
            const angle  = (i / 8) * Math.PI * 2
            const radius = 0.92
            const size   = 0.055 + (i % 3) * 0.02
            const mesh   = new THREE.Mesh(new THREE.IcosahedronGeometry(size, 0), mat())
            mesh.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0)
            mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0)
            mesh.userData = {
                spinX: 0.015, spinY: 0.020, spinZ: 0.008,
                bobAmp: 0.002, bobPhase: Math.random() * Math.PI * 2,
                orbitAngle: angle, orbitRadius: radius,
                orbitSpeed: 0.005 * (i % 2 ? 1 : -1),
                random: Math.random(),
            }
            g4.add(mesh)
        }
        this.ballsLittleGroups.push(g4); this.ballsGroup.add(g4)

        this.scene.add(this.ballsGroup)
        this.positionBallsGroup()
    }

    showBalls(index) {
        const dir = (this.toolsSectionBuilder && this.toolsSectionBuilder.prevIndex < this.toolsSectionBuilder.currentIndex) ? 1 : -1

        this.ballsLittleGroups.forEach((group, i) => {
            gsap.to(group.rotation, {
                z: group.rotation.z + dir * Math.PI * 0.3,
                duration: 1.2,
                ease: "power3.inOut"
            })
            group.traverse((child) => {
                if (child.isMesh && child.material && child.material.uniforms) {
                    if (i === index) {
                        child.visible = true
                        gsap.to(child.material.uniforms.u_alpha, { value: 1, duration: 0.7, delay: 0.2, ease: "power2.out" })
                    } else {
                        gsap.to(child.material.uniforms.u_alpha, {
                            value: 0, duration: 0.35, ease: "power2.in",
                            onComplete: () => { child.visible = false }
                        })
                    }
                }
            })
        })
    }

    positionBallsGroup(e) {
        this.ballsHtmlTarget = document.querySelector('.about-tools')
        const wPos = utils.getWorldPositionFromElement(this.ballsHtmlTarget, this.camera, 3)
        this.ballsGroup.position.set(wPos.x, wPos.y, 0)
        if (e) this.ballsGroup.position.set(wPos.x, wPos.y, 0)
    }

    onScroll(e) {
        if (this.isDestroyed) return
        this.positionLogo(e)
        this.positionBallsGroup(e)
        const rect = this.partnerContainer.getBoundingClientRect()
        const bottomY = rect.top + (rect.height - window.innerHeight * 1.2) + e.animatedScroll
        if (e.animatedScroll > bottomY) this.logoDisappear()
        else this.logoAppear()
    }

    resize() {
        if (this.isDestroyed) return
        this.positionLogo()
    }

    setPartnerCards() {
        const container = document.querySelector('.about-partnersContainer')
        this.experience.router.main_data.data.about_partners.forEach((item) => {
            const card = `<div class="about-partnerCards"><img src=${item.partner_logo.url} alt="" /></div>`
            container.insertAdjacentHTML('beforeend', card)
        })
    }

    update() {
        if (this.isDestroyed) return

        const t     = this.experience.time.elapsed * 0.001
        const delta = this.experience.time.delta

        // ── About-page logo animation ────────────────────────────────────────────
        const initialRotation     = { x: 0, y: this.isPhone ? Math.PI : -0.2 + Math.PI, z: 0 }
        const firstSectionTarget  = { x: -(Math.PI * 0.2), y: (Math.PI + 0.5) + Math.PI, z: 0 }
        const secondSectionTarget = { x: -firstSectionTarget.x + 0.3, y: Math.PI + Math.PI * 0.75, z: 0 }
        const targetRotationX = (firstSectionTarget.x * (1 - this.firstSectionPourcentage)) + (secondSectionTarget.x * this.secondSectionPourcentage)
        const targetRotationY = (initialRotation.y * this.firstSectionPourcentage) + (firstSectionTarget.y * (1 - this.firstSectionPourcentage)) + (secondSectionTarget.y * this.secondSectionPourcentage)
        const targetScale = this.isPhone ? 0.5 + this.secondSectionPourcentage * 0.4 : 0.18 + this.secondSectionPourcentage * 0.4

        this.logo.rotation.x = THREE.MathUtils.lerp(this.logo.rotation.x, targetRotationX, 0.1)
        this.logo.rotation.y = THREE.MathUtils.lerp(this.logo.rotation.y, targetRotationY, 0.1)
        this.logo.scale.x    = THREE.MathUtils.lerp(this.logo.scale.x, targetScale, 0.1)
        this.logo.scale.y    = THREE.MathUtils.lerp(this.logo.scale.y, targetScale, 0.1)
        this.logo.scale.z    = THREE.MathUtils.lerp(this.logo.scale.z, targetScale, 0.1)

        this.logoItems.forEach((item) => {
            const originRotation = -Math.PI * 0.5
            const cos = Math.cos(t + item.userData.random * 10) * Math.PI * 0.05
            item.rotation.z = originRotation + cos - (originRotation * (1 - this.firstSectionPourcentage))
        })

        // ── Team debris animation ────────────────────────────────────────────────
        this.ballsLittleGroups.forEach((group) => {
            // Slow whole-group drift
            group.rotation.z += delta * 0.12

            // Animate every child (and their children recursively)
            const animNode = (obj) => {
                const d = obj.userData
                if (d && d.spinY !== undefined) {
                    // Per-piece spin
                    obj.rotation.x += d.spinX || 0
                    obj.rotation.y += d.spinY || 0
                    obj.rotation.z += d.spinZ || 0

                    // Orbit in a circle (keeps pieces spread out dynamically)
                    if (d.orbitAngle !== undefined) {
                        d.orbitAngle += d.orbitSpeed || 0
                        obj.position.x = Math.cos(d.orbitAngle) * d.orbitRadius
                        obj.position.y = Math.sin(d.orbitAngle) * d.orbitRadius
                    }

                    // Vertical bob on top of orbit
                    if (d.bobAmp) {
                        obj.position.z = Math.sin(t * 1.8 + d.bobPhase) * d.bobAmp * 8
                    }
                }
                if (obj.children) obj.children.forEach(animNode)
            }
            group.children.forEach(animNode)
        })
    }

    destroy() {
        this.isDestroyed = true

        if (this.logo) {
            this.logo.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose()
                    if (child.material) {
                        Array.isArray(child.material)
                            ? child.material.forEach(m => m.dispose())
                            : child.material.dispose()
                    }
                }
            })
            this.scene.remove(this.logo)
            this.logo = null
            this.logoItems = []
        }

        if (this.ballsGroup) {
            this.ballsGroup.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose()
                    if (child.material) {
                        Array.isArray(child.material)
                            ? child.material.forEach(m => m.dispose())
                            : child.material.dispose()
                    }
                }
            })
            this.scene.remove(this.ballsGroup)
            this.ballsGroup = null
            this.ballsLittleGroups = []
        }

        if (this.toolsSectionBuilder?.destroy) { this.toolsSectionBuilder.destroy(); this.toolsSectionBuilder = null }
        if (this.ctaBuilder?.destroy)          { this.ctaBuilder.destroy();          this.ctaBuilder = null }
        if (this.titleBuilder?.destroy)        { this.titleBuilder.destroy();        this.titleBuilder = null }
        if (this.textBuilder?.destroy)         { this.textBuilder.destroy();         this.textBuilder = null }
        if (this.partnerContainer)             { this.partnerContainer.innerHTML = ''; this.partnerContainer = null }
    }
}
